import { useState } from "react";
import { testNotification } from "../services/api";
import Card from "./ui/Card";
import Button from "./ui/Button";
import { ConfirmModal, InputModal } from "./ui/Modal";

export default function NotificationTest() {
  const [loadingStates, setLoadingStates] = useState({});
  const [message, setMessage] = useState("");
  const [emailModal, setEmailModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '' });

  const setLoading = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  const testEmailNotification = async () => {
    setLoading('email', true);
    setMessage("");
    try {
      const res = await testNotification("email");
      if (res.ok) {
        setMessage("✅ Test email notification sent! Check your inbox.");
      } else {
        setMessage("❌ Failed to send test email: " + (res.error || "Unknown error"));
      }
    } catch (error) {
      setMessage("❌ Failed to send test email: " + error.message);
      console.error("Email test error:", error);
    } finally {
      setLoading('email', false);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  const testPushNotification = async () => {
    setLoading('push', true);
    setMessage("");
    try {
      const res = await testNotification("push");
      if (res.ok) {
        setMessage("✅ Test push notification sent!");
      } else {
        setMessage("❌ Failed to send test push: " + (res.error || "Unknown error"));
      }
    } catch (error) {
      setMessage("❌ Failed to send test push: " + error.message);
      console.error("Push test error:", error);
    } finally {
      setLoading('push', false);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  const testDailyReport = async () => {
    setLoading('report', true);
    setMessage("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:4000'}/test-daily-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await res.json();
      if (data.ok) {
        setMessage("✅ Daily status report sent! Check your email inbox.");
      } else {
        setMessage("❌ Failed to send daily report: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      setMessage("❌ Failed to send daily report: " + error.message);
      console.error("Daily report test error:", error);
    } finally {
      setLoading('report', false);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  const testSSLNotification = async () => {
    setLoading('ssl', true);
    setMessage("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:4000'}/test-ssl-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          url: 'https://expired.badssl.com' // Known expired certificate for testing
        })
      });
      const data = await res.json();
      if (data.ok) {
        setMessage("✅ SSL certificate test notification sent! Check your email inbox and browser notifications.\n\n🔐 This test uses an expired SSL certificate to simulate an SSL alert.");
      } else {
        setMessage("❌ Failed to send SSL test notification: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      setMessage("❌ Failed to send SSL test notification: " + error.message);
      console.error("SSL test notification error:", error);
    } finally {
      setLoading('ssl', false);
      setTimeout(() => setMessage(""), 8000);
    }
  };

  const testEmailConfig = async () => {
    setEmailModal(true);
  };

  const handleEmailConfigSubmit = async (email) => {
    if (!email.trim()) {
      setMessage("❌ Please enter a valid email address.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    
    setEmailModal(false);
    setLoading('config', true);
    setMessage("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:4000'}/test-email-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (data.ok) {
        setMessage("✅ Email configuration test sent! Check your inbox for the test email.");
      } else {
        setMessage("❌ Failed to test email config: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      setMessage("❌ Failed to test email config: " + error.message);
      console.error("Email config test error:", error);
    } finally {
      setLoading('config', false);
      setTimeout(() => setMessage(""), 5000);
    }
  };

  const testRealNotifications = async () => {
    setConfirmModal({
      isOpen: true,
      message: "This will trigger a real DOWN notification to test your alert system. This will send actual emails and notifications. Continue?",
      onConfirm: () => {
        setConfirmModal({ isOpen: false, onConfirm: null, message: '' });
        executeRealNotificationTest();
      }
    });
  };

  const executeRealNotificationTest = async () => {
    setLoading('real', true);
    setMessage("");
    
    const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
    
    console.log(`🔍 Testing real notifications using test-alert endpoint`);
    
    try {
      // Use the existing test-alert endpoint to trigger real notifications
      const res = await fetch(`${apiBase}/test-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          url: "https://test-notification-site.example.com",
          status: "DOWN"
        })
      });
      
      console.log(`📡 Response status: ${res.status} ${res.statusText}`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setMessage("❌ Backend endpoint not found. Please make sure the backend server is running.");
          return;
        }
        if (res.status === 401) {
          setMessage("❌ Authentication failed. Please try logging in again.");
          return;
        }
        if (res.status === 400) {
          setMessage("❌ Bad request. Check that url and status parameters are provided.");
          return;
        }
      }
      
      const contentType = res.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await res.text();
        console.error("❌ Non-JSON response received:", textResponse.substring(0, 200));
        setMessage("❌ Server returned HTML instead of JSON. Please make sure the backend server is running properly.");
        return;
      }
      
      const data = await res.json();
      console.log("📊 Response data:", data);
      
      if (data.ok) {
        setMessage("✅ Real notification test completed successfully!\n\n📧 Check your email inbox and browser for DOWN notifications!\n\n� This test simulates a website going down and triggers the actual notification system.");
      } else {
        setMessage("❌ Failed to test real notifications: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("🚨 Real notifications test error:", error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setMessage("❌ Cannot connect to backend server. Please make sure the backend is running on port 4000.");
      } else if (error.message.includes('JSON')) {
        setMessage("❌ Server returned invalid JSON. This usually means the backend server is not running properly.");
      } else {
        setMessage("❌ Failed to test real notifications: " + error.message);
      }
    } finally {
      setLoading('real', false);
      setTimeout(() => setMessage(""), 15000);
    }
  };

  const testBrowserNotification = async () => {
    setLoading('browser', true);
    setMessage("");
    
    console.log("🔔 Testing browser notifications...");
    console.log("🔍 Notification support:", "Notification" in window);
    console.log("🔍 Current permission:", Notification.permission);
    console.log("🔍 Location protocol:", window.location.protocol);
    console.log("🔍 Is secure context:", window.isSecureContext);
    
    try {
      // Check if notifications are supported
      if (!("Notification" in window)) {
        const msg = "❌ Browser notifications are not supported in this browser.";
        setMessage(msg);
        console.error(msg);
        return;
      }

      // Check if we're in a secure context (HTTPS or localhost)
      if (!window.isSecureContext && window.location.protocol !== 'http:') {
        const msg = "❌ Browser notifications require HTTPS or localhost.";
        setMessage(msg);
        console.error(msg);
        return;
      }

      if (Notification.permission === "granted") {
        console.log("✅ Permission already granted, creating notification...");
        
        try {
          const notification = new Notification("SiteScope Test", {
            body: "This is a test notification from SiteScope! 🔔",
            icon: "/favicon.ico",
            tag: "sitescope-test",
            requireInteraction: false,
            silent: false,
            badge: "/favicon.ico"
          });
          
          console.log("✅ Notification created:", notification);
          
          // Add event listeners for debugging
          notification.onshow = () => {
            console.log("🔔 Notification shown");
          };
          
          notification.onclick = () => {
            console.log("👆 Notification clicked");
            notification.close();
          };
          
          notification.onerror = (error) => {
            console.error("❌ Notification error:", error);
          };
          
          // Auto-close after 5 seconds
          setTimeout(() => {
            if (notification) {
              notification.close();
              console.log("🔕 Notification auto-closed");
            }
          }, 5000);
          
          setMessage("✅ Test browser notification sent! You should see it now. Check your browser's notification area or system tray.");
          
        } catch (notifError) {
          console.error("❌ Error creating notification:", notifError);
          setMessage("❌ Failed to create notification: " + notifError.message);
        }
        
      } else if (Notification.permission === "default") {
        console.log("🔔 Requesting notification permission...");
        setMessage("🔔 Requesting notification permission... Please allow notifications when prompted.");
        
        try {
          const permission = await Notification.requestPermission();
          console.log("🔍 Permission result:", permission);
          
          if (permission === "granted") {
            console.log("✅ Permission granted, creating notification...");
            
            const notification = new Notification("SiteScope Test", {
              body: "Perfect! Browser notifications are now enabled for SiteScope! 🎉",
              icon: "/favicon.ico",
              tag: "sitescope-welcome",
              requireInteraction: false,
              badge: "/favicon.ico"
            });
            
            notification.onshow = () => console.log("🔔 Welcome notification shown");
            notification.onclick = () => {
              console.log("👆 Welcome notification clicked");
              notification.close();
            };
            
            setTimeout(() => {
              if (notification) {
                notification.close();
                console.log("🔕 Welcome notification auto-closed");
              }
            }, 5000);
            
            setMessage("✅ Browser notifications enabled and test sent! You should see the notification now.");
          } else {
            const msg = `❌ Browser notification permission was ${permission}. Please enable notifications in your browser settings and try again.`;
            setMessage(msg);
            console.log(msg);
          }
        } catch (permError) {
          console.error("❌ Permission request error:", permError);
          setMessage("❌ Failed to request permission: " + permError.message);
        }
        
      } else {
        const msg = "❌ Browser notifications are blocked. Please go to your browser settings and enable notifications for this site, then try again.";
        setMessage(msg);
        console.log(msg);
        console.log("💡 To enable: Browser settings → Privacy & Security → Notifications → Allow for this site");
      }
      
    } catch (error) {
      console.error("🚨 Browser notification test error:", error);
      
      if (error.name === 'NotAllowedError') {
        setMessage("❌ Browser notifications are blocked. Please enable them in your browser settings.");
      } else if (error.name === 'SecurityError') {
        setMessage("❌ Security error: Browser notifications require HTTPS or localhost.");
      } else {
        setMessage("❌ Failed to send browser notification: " + error.message);
      }
    } finally {
      setLoading('browser', false);
      setTimeout(() => setMessage(""), 10000); // Longer timeout for debugging
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>Test Notifications</Card.Title>
        <Card.Description>
          Test your notification settings to ensure they're working properly
        </Card.Description>
      </Card.Header>

      <Card.Content className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
          {/* Email Test */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Email</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Test email notifications</p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={testEmailNotification}
              disabled={loadingStates.email}
              className="w-full"
            >
              {loadingStates.email ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </div>
              ) : (
                "Test Email"
              )}
            </Button>
          </div>

          {/* Browser Test */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM4 19h6v-6H4v6zM4 5h6V1H4v4zM15 5h5V1h-5v4z"></path>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Browser</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Test browser notifications</p>
            </div>
            <Button
              variant="success"
              size="sm"
              onClick={testBrowserNotification}
              disabled={loadingStates.browser}
              className="w-full"
            >
              {loadingStates.browser ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </div>
              ) : (
                "Test Browser"
              )}
            </Button>
          </div>

          {/* Push Test */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Mobile Push</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Test mobile push notifications</p>
            </div>
            <Button
              variant="warning"
              size="sm"
              onClick={testPushNotification}
              disabled={loadingStates.push}
              className="w-full"
            >
              {loadingStates.push ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </div>
              ) : (
                "Test Push"
              )}
            </Button>
          </div>

          {/* Daily Report Test */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Daily Report</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Test daily status report</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={testDailyReport}
              disabled={loadingStates.report}
              className="w-full"
            >
              {loadingStates.report ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Sending...
                </div>
              ) : (
                "Test Report"
              )}
            </Button>
          </div>

          {/* Email Config Test */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Email Config</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Test SMTP configuration</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={testEmailConfig}
              disabled={loadingStates.config}
              className="w-full"
            >
              {loadingStates.config ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Testing...
                </div>
              ) : (
                "Test Config"
              )}
            </Button>
          </div>

          {/* Real Notifications Test */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">Real Test</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Test with failing website</p>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={testRealNotifications}
              disabled={loadingStates.real}
              className="w-full"
            >
              {loadingStates.real ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </div>
              ) : (
                "Test Real"
              )}
            </Button>
          </div>

          {/* SSL Certificate Test */}
          <div className="text-center space-y-3">
            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">SSL Certificate</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Test SSL certificate alerts</p>
            </div>
            <Button
              variant="warning"
              size="sm"
              onClick={testSSLNotification}
              disabled={loadingStates.ssl}
              className="w-full"
            >
              {loadingStates.ssl ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Testing...
                </div>
              ) : (
                "Test SSL"
              )}
            </Button>
          </div>
        </div>

        {message && (
          <div className={`rounded-md p-3 ${
            message.includes("✅") ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <div className={`text-sm ${
              message.includes("✅") ? 'text-green-700' : 'text-red-700'
            }`}>
              {message}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
            <p>• <strong>Email</strong> - Tests email notification system with fake alert</p>
            <p>• <strong>Browser</strong> - Tests browser notification permission and display</p>
            <p>• <strong>Mobile Push</strong> - Tests push notification to mobile devices</p>
            <p>• <strong>Daily Report</strong> - Tests automated daily status report email</p>
            <p>• <strong>Email Config</strong> - Tests SMTP server connection and configuration</p>
            <p>• <strong>Real Test</strong> - Adds a failing website to test actual monitoring notifications</p>
            <p>• <strong>SSL Certificate</strong> - Tests SSL certificate expiration alerts using expired.badssl.com</p>
          </div>
          
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
              <strong>🔍 Browser Notification Troubleshooting:</strong>
            </p>
            <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
              <li>• Make sure you're on HTTPS or localhost</li>
              <li>• Check browser console (F12) for detailed error messages</li>
              <li>• Look for notification permission dialog (may appear in address bar)</li>
              <li>• Check your browser's notification settings</li>
              <li>• Some browsers block notifications in incognito/private mode</li>
            </ul>
          </div>
          
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-xs text-green-700 dark:text-green-300 mb-2">
              <strong>🔐 SSL Certificate Monitoring:</strong>
            </p>
            <ul className="text-xs text-green-600 dark:text-green-400 space-y-1">
              <li>• SiteScope now monitors SSL certificates alongside uptime</li>
              <li>• Automatically checks certificate expiration dates</li>
              <li>• Sends alerts 7, 14, and 30 days before expiration</li>
              <li>• Only monitors HTTPS websites (SSL checking disabled for HTTP)</li>
              <li>• Alerts for expired, expiring, or invalid certificates</li>
            </ul>
          </div>
          
          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              <strong>💡 Tips:</strong> Use "Real Test" to verify uptime monitoring and "SSL Certificate" to test SSL alert functionality. 
              The SSL test uses a known expired certificate to demonstrate SSL certificate alerts.
            </p>
          </div>
        </div>
      </Card.Content>

      {/* Email Configuration Modal */}
      <InputModal
        isOpen={emailModal}
        onClose={() => setEmailModal(false)}
        onSubmit={handleEmailConfigSubmit}
        title="Test Email Configuration"
        label="Enter an email address to test your SMTP configuration:"
        placeholder="test@example.com"
        inputType="email"
        required={true}
        submitText="Send Test"
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, onConfirm: null, message: '' })}
        onConfirm={confirmModal.onConfirm}
        title="Confirm Action"
        message={confirmModal.message}
        confirmText="Continue"
        variant="danger"
      />
    </Card>
  );
}
